/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import React, { lazy, ReactNode, Suspense } from 'react';
import httpRequest from './http-request';
import Spinner from '@components/spinner';

/**
 * Widget Registry - Dynamic widget component loading
 * Fetches available widget types from backend and lazily loads components
 */

export interface WidgetType {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    iconUrl?: string;
    componentId: string;
    isEnabled: boolean;
    isBuiltin: boolean;
    supportedSizes: string[];
    config?: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
}

export interface WidgetComponentProps {
    config?: Record<string, unknown>;
    widgetId?: string;
    widgetSize?: string;
    widgetType?: WidgetType;
    [key: string]: unknown;
}

type WidgetComponent = React.ComponentType<WidgetComponentProps>;

// Component lazy loaders - each widget component
const componentMap = new Map<string, React.LazyExoticComponent<WidgetComponent>>();
let isInitialized = false;

const loadWidget = (loader: () => Promise<{ default: React.ComponentType<WidgetComponentProps> }>) =>
    lazy(loader) as React.LazyExoticComponent<WidgetComponent>;

/**
 * Initialize widget component registry with lazy loaders
 */
export function initializeWidgetRegistry() {
    if (isInitialized) return;
    isInitialized = true;

    // Import widget components dynamically
    componentMap.set(
        'ClockWidget',
        loadWidget(() => import('@components/widget/clock').then((m) => ({ default: m.ClockWidget })))
    );

    componentMap.set(
        'PinnedNoteWidget',
        loadWidget(() => import('@components/widget/pinned-note').then((m) => ({ default: m.PinnedNoteWidget })))
    );

    componentMap.set(
        'RandomRemarkWidget',
        loadWidget(() => import('@components/widget/remark').then((m) => ({ default: m.RandomRemarkWidget })))
    );

    componentMap.set(
        'RecentBirthdayWidget',
        loadWidget(() => import('@components/widget/birthday').then((m) => ({ default: m.RecentBirthdayWidget })))
    );

    componentMap.set(
        'RecentActivityWidget',
        loadWidget(() => import('@components/widget/activity').then((m) => ({ default: m.RecentActivityWidget })))
    );

    componentMap.set(
        'ActivityCounterWidget',
        loadWidget(() =>
            import('@components/widget/activity-counter').then((m) => ({ default: m.ActivityCounterWidget }))
        )
    );

    componentMap.set(
        'NotesWidget',
        loadWidget(() => import('@components/widget/notes').then((m) => ({ default: m.NotesWidget })))
    );

    componentMap.set(
        'CookingWidget',
        loadWidget(() => import('@components/widget/cooking').then((m) => ({ default: m.CookingWidget })))
    );

    componentMap.set(
        'CustomWidget',
        loadWidget(() => import('@components/widget/custom').then((m) => ({ default: m.CustomWidget })))
    );

    componentMap.set(
        'ShortcutWidget',
        loadWidget(() => import('../components/widget/shortcut').then((m) => ({ default: m.ShortcutWidget })))
    );
}

/**
 * Get a widget component by componentId
 * Returns a lazy-loaded component wrapped in Suspense
 */
export function getWidgetComponent(componentId: string, fallback?: ReactNode) {
    const Component = componentMap.get(componentId);

    if (!Component) {
        console.warn(`Widget component not found: ${componentId}`);
        return () => (
            <div className="flex h-full w-full items-center justify-center rounded bg-gray-100">
                <span className="text-gray-500">Widget not found</span>
            </div>
        );
    }

    return (props: WidgetComponentProps) => (
        <Suspense fallback={fallback ?? <Spinner />}>
            <Component {...props} />
        </Suspense>
    );
}

/**
 * Fetch available widget types from backend
 */
export async function fetchAvailableWidgetTypes(): Promise<WidgetType[]> {
    try {
        const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
        const response = await httpRequest(`${apiBase}/dashboard/widget-types`, 'GET');

        if (!response.ok) {
            throw new Error(`Failed to fetch widget types: ${response.status}`);
        }

        const data = await response.json();
        const types = Array.isArray(data.data) ? data.data : [];
        return types.length > 0 ? types : getDefaultWidgetTypes();
    } catch (error) {
        console.error('Error fetching widget types:', error);
        // Return default widget types as fallback
        return getDefaultWidgetTypes();
    }
}

/**
 * Admin: Fetch every widget type (including disabled) for management (superadmin only)
 */
export async function adminFetchAllWidgetTypes(): Promise<WidgetType[]> {
    const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
    const response = await httpRequest(`${apiBase}/admin/dashboard/widget-types`, 'GET');

    if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message ?? `Failed to fetch widget types: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data.data) ? (data.data as WidgetType[]) : [];
}

/**
 * Admin: Update a widget type configuration (superadmin only)
 */
export async function adminUpdateWidgetType(
    widgetTypeId: string,
    updates: {
        displayName?: string;
        description?: string;
        iconUrl?: string;
        isEnabled?: boolean;
        supportedSizes?: string[];
        config?: Record<string, unknown>;
    }
): Promise<WidgetType> {
    const apiBase = import.meta.env.VITE_SERVER_ENDPOINT;
    const response = await httpRequest(`${apiBase}/admin/dashboard/widget-types/${widgetTypeId}`, 'PUT', updates);

    if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message ?? `Failed to update widget type: ${response.status}`);
    }

    const data = await response.json();
    return data.data as WidgetType;
}

/**
 * Default widget types (fallback)
 */
export function getDefaultWidgetTypes(): WidgetType[] {
    return [
        {
            id: 'clock-001',
            name: 'clock',
            displayName: 'Clock',
            description: 'Display current time and date',
            componentId: 'ClockWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['1x1', '1x2'],
        },
        {
            id: 'pinned-notes-001',
            name: 'pinned_notes',
            displayName: 'Pinned Notes',
            description: 'Show pinned notes',
            componentId: 'PinnedNoteWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['2x2', '2x4'],
        },
        {
            id: 'remarks-001',
            name: 'remarks',
            displayName: 'Remarks',
            description: 'Display random remarks',
            componentId: 'RandomRemarkWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['1x1', '1x2'],
        },
        {
            id: 'birthdays-001',
            name: 'birthdays',
            displayName: 'Birthdays',
            description: 'Show upcoming birthdays',
            componentId: 'RecentBirthdayWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['2x4'],
        },
        {
            id: 'activities-001',
            name: 'activities',
            displayName: 'Activities',
            description: 'Show recent activities',
            componentId: 'RecentActivityWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['2x4'],
        },
        {
            id: 'activity-counter-001',
            name: 'activity_counter',
            displayName: 'Activity Counter',
            description: 'Count days until or since an activity',
            componentId: 'ActivityCounterWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['2x2'],
        },
        {
            id: 'shortcut-001',
            name: 'shortcut',
            displayName: 'Shortcut',
            description: 'Quick access to a favorite site',
            componentId: 'ShortcutWidget',
            isEnabled: true,
            isBuiltin: true,
            supportedSizes: ['1x1'],
        },
    ];
}

// Initialize registry on import
initializeWidgetRegistry();
